import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extrae el mensaje de error de una respuesta de axios.
 * Devuelve el string del campo `error` si existe; si no, devuelve el fallback.
 * Nunca expone internals del servidor al usuario.
 */
export function extractApiError(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: {
        data?: {
          error?: unknown
          details?: Array<{ path?: Array<string | number>; message?: unknown }>
        }
      }
    }
    const data = err.response?.data
    const issue = Array.isArray(data?.details) ? data.details[0] : undefined
    if (issue && typeof issue.message === 'string' && issue.message.trim()) {
      const field = Array.isArray(issue.path) ? issue.path.filter((part) => typeof part === 'string').at(-1) : undefined
      const labels: Record<string, string> = {
        tenant_id: 'empresa-cliente', tipo_proyecto_id: 'tipo de proyecto', tipo_proyecto_ids: 'tipos de proyecto',
        producto_ids: 'productos', responsable_id: 'responsable', fabrica_id: 'fábrica', certificador_id: 'OCP',
        laboratorio_id: 'laboratorio', fecha_inicio: 'fecha de inicio', fecha_estimada_termino: 'fecha estimada de término',
        fecha: 'fecha', concepto: 'concepto', monto: 'monto', proveedor: 'proveedor', motivo: 'motivo de anulación',
      }
      const label = field ? labels[field] ?? field.replaceAll('_', ' ') : undefined
      const rawMessage = issue.message.trim()
      const friendlyMessage = /^(required|invalid input|expected .+, received)/i.test(rawMessage)
        ? 'el valor es obligatorio o no tiene un formato válido'
        : rawMessage
      return label ? `Revisa ${label}: ${friendlyMessage}` : friendlyMessage
    }
    const msg = data?.error
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  return fallback
}

/**
 * Normaliza un error de axios cuyo responseType era 'blob'.
 * Cuando el servidor responde con error (4xx/5xx) y axios está configurado en
 * modo blob, el cuerpo del error llega como Blob en lugar de JSON.
 * Esta función lo convierte a texto, parsea el JSON y retorna un error cuyo
 * response.data tiene la estructura { error: string } — compatible con
 * extractApiError.
 *
 * Uso: `throw await normalizeBlobError(error)` en el catch del servicio.
 */
export async function normalizeBlobError(error: unknown): Promise<unknown> {
  if (typeof error === 'object' && error !== null) {
    const axiosErr = error as { response?: { data?: unknown } }
    if (axiosErr.response?.data instanceof Blob) {
      try {
        const text = await (axiosErr.response.data as Blob).text()
        const json = JSON.parse(text) as Record<string, unknown>
        return {
          ...axiosErr,
          response: {
            ...(axiosErr.response as object),
            data: json,
          },
        }
      } catch {
        // Si el cuerpo no es JSON válido, devolver el error original
      }
    }
  }
  return error
}
